import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { ActivationComponentAssets } from "../../../../../helpers/ActivationAssets";
import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import type { DomainListAutocompleteProps } from "../../../Competitors/components/DomainListAutocomplete";
import { DomainListAutocomplete } from "../../../Competitors/components/DomainListAutocomplete";
import { DomainListSectionHeader } from "../../../Competitors/components/DomainListSectionHeader";
import { SettingsMainCompetitor } from "../../../Competitors/SettingsMainCompetitor/SettingsMainCompetitor";
import { DATA_AUTOMATION_NAMES } from "../constants";
import type { SecondaryDomain } from "../hooks/useSecondaryDomainsState";

const DATA_AUTOMATION_PLACE = "settings";
const SECONDARY_DATA_AUTOMATION_NAMES = [...DATA_AUTOMATION_NAMES, "secondary-competitors"];

export type SecondaryCompetitorsSectionProps = Pick<DomainListAutocompleteProps, "primaryDomain"> & {
    secondaryDomains: SecondaryDomain[];
    activationDomains: string[];
    isLoading: boolean;
    onAdd: (item: SecondaryDomain) => void;
    onRemove: (domain: string) => void;
};

export const SecondaryCompetitorsSection = ({
    activationDomains,
    secondaryDomains,
    primaryDomain,
    isLoading,
    onAdd,
    onRemove,
}: SecondaryCompetitorsSectionProps) => {
    const { translate } = useTranslate();

    const activationDomainsSet = useMemo(() => new Set(activationDomains), [activationDomains]);
    const exclude = useMemo(() => {
        const domains = secondaryDomains.map(({ domain }) => domain);

        return [...activationDomains, ...domains];
    }, [activationDomains, secondaryDomains]);

    const secondaryListLimit = ActivationComponentAssets.alertsSecondaryListLimit;
    const currentSize = secondaryDomains.length;

    const handleItemClick: DomainListAutocompleteProps["onItemClick"] = (item, clearTextField) => {
        onAdd({ domain: item.name, icon: item.icon || item.image });
        clearTextField();
    };

    return (
        <Stack
            gap="24px"
            width="500px"
            data-automation={generateAutomationName(DATA_AUTOMATION_PLACE, [
                ...SECONDARY_DATA_AUTOMATION_NAMES,
                "wrapper",
            ])}
        >
            <DomainListSectionHeader
                currentSize={currentSize}
                dataAutomationNames={SECONDARY_DATA_AUTOMATION_NAMES}
                limit={secondaryListLimit}
                subtitle={translate("activation.settings.news.secondary.subtitle")}
                title={translate("activation.settings.news.secondary.title")}
                tooltipText={translate("activation.settings.news.secondary.tooltip")}
            />

            <Stack gap="12px">
                <DomainListAutocomplete
                    currentSize={currentSize}
                    limit={secondaryListLimit}
                    isLoading={isLoading}
                    onItemClick={handleItemClick}
                    exclude={exclude}
                    dataAutomationNames={SECONDARY_DATA_AUTOMATION_NAMES}
                    primaryDomain={primaryDomain}
                />

                <Stack>
                    {secondaryDomains.map(({ domain, icon }) => {
                        const isOverlapping = activationDomainsSet.has(domain);

                        return (
                            <SettingsMainCompetitor
                                key={domain}
                                name={domain}
                                image={icon ?? ""}
                                onSelect={() => undefined}
                                onDelete={() => onRemove(domain)}
                            >
                                {isOverlapping ? (
                                    <Text
                                        fontVariant="caption"
                                        color={colorsPalettes.carbon[300]}
                                        data-automation={generateAutomationName(DATA_AUTOMATION_PLACE, [
                                            ...SECONDARY_DATA_AUTOMATION_NAMES,
                                            "overlap-message",
                                            domain,
                                        ])}
                                    >
                                        {translate("activation.settings.news.secondary.overlap.message")}
                                    </Text>
                                ) : null}
                            </SettingsMainCompetitor>
                        );
                    })}
                </Stack>
            </Stack>
        </Stack>
    );
};
